import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Catches rendering errors in the subtree so a single broken page does not
 * blank out the entire application. Renders a friendly fallback with a way to
 * recover instead of an empty white screen.
 *
 * Special-cases `ChunkLoadError` ("Loading chunk X failed") which happens when
 * a new build is deployed but the browser still has the old index.html cached
 * referencing chunk hashes that no longer exist on the server. In that case we
 * offer a hard reload to fetch the fresh manifest.
 */
function isChunkError(error) {
  if (!error) return false;
  const name = error.name || '';
  const msg = String(error.message || error);
  return name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(msg) || /Loading CSS chunk .* failed/i.test(msg);
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHardReload = () => {
    // Force a fresh fetch of index.html (bypass cache) to pick up new chunk hashes.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkError(this.state.error);
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', p: 3 }}>
          <Paper sx={{ p: 4, maxWidth: 520, textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {chunkError ? 'Update available' : 'Something went wrong'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {chunkError
                ? 'A new version of the app was deployed. Reload the page to load the latest version.'
                : 'This section ran into an unexpected error. You can try again or go back to the dashboard.'}
            </Typography>
            {this.state.error && !chunkError && (
              <Typography
                variant="caption"
                component="pre"
                sx={{ display: 'block', textAlign: 'left', bgcolor: 'grey.100', p: 1.5, borderRadius: 1, mb: 3, overflow: 'auto', maxHeight: 200 }}
              >
                {String(this.state.error?.message || this.state.error)}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {chunkError ? (
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleHardReload}>
                  Reload page
                </Button>
              ) : (
                <>
                  <Button variant="contained" onClick={this.handleReset}>
                    Try Again
                  </Button>
                  <Button variant="outlined" onClick={() => { window.location.href = '/dashboard'; }}>
                    Go to Dashboard
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
